import type { AppRouter } from "@teamz/api/trpc";

import { enUS, frFR } from "@clerk/localizations";
import { ClerkProvider as _ClerkProvider, ClerkLoaded, ClerkLoading, useAuth, useUser } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import { Outlet } from "react-router";

import { env } from "@/env";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { createAuthenticatedFetch } from "@/lib/authenticated-fetch";
import { queryClient } from "@/lib/query-client";
import { TRPCProvider } from "@/lib/trpc";
import { Fallback } from "@/pages/error-page";
import { routes } from "@/routes";

import { LoaderTeamz } from "./ui/loader-teamz";
import { Toaster } from "./ui/toaster";
import { TooltipProvider } from "./ui/tooltip";

export function Providers() {
  const { countryCodeLocale } = useTranslationUtils();
  const clerkLocale = countryCodeLocale === "fr" ? frFR : enUS;

  return (
    <Sentry.ErrorBoundary fallback={(
      <div className="flex h-screen w-screen items-center justify-center">
        <Fallback />
      </div>
    )}
    >
      <PostHogProvider
        apiKey="phc_Q6aoGyCtrlNyUGk3rCCPCo2Wf2cthEGMMQ5NjNtxaqi"
        options={{
          api_host: "https://eu.i.posthog.com",
          loaded: (ph) => {
            if (import.meta.env.MODE === "development") {
              ph.set_config({
                disable_session_recording: true,
                autocapture: false,
              });
            }
          },
        }}
      >
        <_ClerkProvider
          publishableKey={env.CLERK_PUBLIC_KEY}
          afterSignOutUrl={routes.signup}
          localization={clerkLocale}
        >
          <ClerkLoading>
            <div className="h-dvh w-full">
              <LoaderTeamz />
            </div>
          </ClerkLoading>
          <ClerkLoaded>
            <SyncUserToSentry />
            <Toaster />
            <TRPCWithQuery>
              <TooltipProvider>
                <NuqsAdapter>
                  <Outlet />
                </NuqsAdapter>
              </TooltipProvider>
            </TRPCWithQuery>
          </ClerkLoaded>
        </_ClerkProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  );
}

function TRPCWithQuery({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: `${env.API_URL}/api/trpc`,
          fetch: createAuthenticatedFetch(getToken),
          async headers() {
            const token = await getToken();
            return token ? { Authorization: `Bearer ${token}` } : {};
          },
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}

// This is a no-op function that is used to sync the user to Sentry
// when the user is signed in.
function SyncUserToSentry() {
  const { user, isSignedIn } = useUser();

  useEffect(() => {
    if (isSignedIn) {
      Sentry.setUser({ email: user.primaryEmailAddress?.emailAddress, id: user.id });
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        clerkId: user.id,
      });
    }
    else {
      Sentry.setUser(null);
    }
  }, [user, isSignedIn]);

  return null;
}
